/**
 * Trim — which trim goes on each edge, and how much of it to buy.
 *
 * Works from edge adjacency (adjacency.ts): every stretch of every section's
 * edge, and what it meets. For each stretch this decides the trim.
 *
 * THE DEFAULTS reproduce what the estimator counts today for a room the wizard
 * builds, and fall back to J-Trim for anything it has no rule for:
 *
 *   edge meets …                     default trim
 *   ------------------------------   -------------------------------------------
 *   the wall's perimeter             Snap-In J-Trim, in the section's colour
 *   bare wall                        Snap-In J-Trim, in the section's colour
 *   SlatWall top ↔ boards above      H-Trim, in the SlatWall's colour
 *   SlatWall bottom ↔ boards below   none — the SlatWall's lip laps the boards
 *   SlatWall side ↔ boards           Snap-In J-Trim, in the SlatWall's colour
 *   boards ↔ boards                  H-Trim
 *   SlatWall ↔ SlatWall              H-Trim
 *   hidden under an inset            none — there is no edge there
 *   a window or door                 none — the opening trims its own surround
 *
 *   around a window or door          Snap-In J-Trim where it is cut into product,
 *                                    in that product's colour; none on a door's
 *                                    bottom (the floor) or against bare wall
 *
 * ONE TRIM PER SHARED LINE. Where two sections meet, the line between them gets
 * a single trim, owned by one of them (an inset owns its boundary with the
 * board it sits in; otherwise whichever comes first on the surface). The other
 * section's side reports `shared` and adds nothing.
 *
 * OVERRIDES. A section may name the trim for any of its four sides
 * (`section.edges`): a trim product's acronym, or 'none'. An override applies
 * to every stretch of that side (except stretches hidden under an inset) and
 * makes that side carry its own trim regardless of who owns the shared line.
 * The trim selection matrix, when it exists, slots in as the source of the
 * defaults and of the choices offered — `defaultTrimFor` is the one place.
 */

import { MaterialItem, ProductColor } from '../../types';
import { EdgeSide, Opening, RoomSurfaces, Section, SectionProductType, Surface, sectionFillMode, openingSitsOnFloor, roomSurfaceList } from '../../types/sections';
import { getProductsByType } from '../../data/productTable';
import { TRIM_STOCK_LENGTH } from '../constants';
import { EDGE_SIDES, EdgeNeighbour, EdgeSegment, edgeSegments, openingEdgeSegments } from './adjacency';

/** Product-table acronyms of the trims the defaults use. */
export const TRIM_J = 'JSnap';
export const TRIM_H = 'H';
/** Outside corner — where the panelling turns through a wall that has been taken out. */
export const TRIM_OC = 'OCR';
/** The value of an override that means "no trim on this side". */
export const TRIM_NONE = 'none';

/** Why a stretch of edge carries no trim. */
export type NoTrimReason = 'covered' | 'shared' | 'lap' | 'override' | 'floor' | 'bare';

export interface TrimRun extends EdgeSegment {
  surfaceId: string;
  /** Trim product acronym ('JSnap', 'H', …), or null when this stretch has none. */
  trim: string | null;
  color: ProductColor;
  /** Whether the trim was chosen by the rules or named by the user. */
  source: 'auto' | 'override';
  /** Whose edge this is: a product section, or a window / door. */
  owner: 'section' | 'opening';
  /** The product the trim finishes — for an opening, the product it is cut into. */
  finishes: SectionProductType;
  /** Set when `trim` is null. */
  none?: NoTrimReason;
  /**
   * Where the trim sits, across the edge, in surface coordinates: the edge line
   * itself, or the middle of the gap when the neighbour stands off from it.
   */
  at: number;
}

interface TrimChoice {
  trim: string | null;
  none?: NoTrimReason;
}

/** Does `section` own the line it shares with `other`? */
function ownsSharedLine(surface: Surface, section: Section, other: Section): boolean {
  const mine = sectionFillMode(section.productType);
  const theirs = sectionFillMode(other.productType);
  if (mine !== theirs) return mine === 'insert'; // an inset owns its boundary with the board it sits in
  return surface.sections.indexOf(section) < surface.sections.indexOf(other);
}

/** The rule-based trim for one stretch of edge. The trim selection matrix will replace this. */
export function defaultTrimFor(surface: Surface, section: Section, side: EdgeSide, neighbour: EdgeNeighbour): TrimChoice {
  switch (neighbour.kind) {
    case 'covered':
      return { trim: null, none: 'covered' };
    case 'opening':
      // The window or door trims its own surround (see openingTrims).
      return { trim: null, none: 'shared' };
    case 'perimeter':
    case 'bare':
      return { trim: TRIM_J };
    case 'section': {
      const other = surface.sections.find((s) => s.id === neighbour.sectionId);
      if (!other || !ownsSharedLine(surface, section, other)) return { trim: null, none: 'shared' };
      if (section.productType === 'SlatWall' && other.productType === 'WCB') {
        if (side === 'top') return { trim: TRIM_H };
        if (side === 'bottom') return { trim: null, none: 'lap' };
        return { trim: TRIM_J };
      }
      return { trim: TRIM_H };
    }
  }
}

const EDGE_EPS = 1e-6;

/**
 * A section's bottom edge meets the floor, and its top edge the ceiling, under
 * and over an opening that runs right down to the one or up to the other: there
 * is no board there to finish, so the edge trim is not carried across it.
 */
function openingsOnEdge(surface: Surface, section: Section, side: EdgeSide): [number, number][] {
  if (side !== 'top' && side !== 'bottom') return [];
  const edge = side === 'top' ? section.y + section.height : section.y;
  return (surface.openings ?? [])
    .filter((o) => (side === 'bottom'
      ? o.y <= edge + EDGE_EPS && o.y + o.height > edge + EDGE_EPS
      : o.y + o.height >= edge - EDGE_EPS && o.y < edge - EDGE_EPS))
    .map((o): [number, number] => [o.x, o.x + o.width]);
}

/** Every stretch of every edge of one section, with its trim. */
export function sectionTrims(surface: Surface, section: Section): TrimRun[] {
  const runs: TrimRun[] = [];
  for (const side of EDGE_SIDES) {
    const override = section.edges?.[side];
    const bare = openingsOnEdge(surface, section, side);
    for (const whole of edgeSegments(surface, section, side)) {
      // Cut the stretch where an opening reaches the floor (or ceiling) — those parts carry nothing.
      const gapless = whole.neighbour.kind === 'perimeter' || whole.neighbour.kind === 'bare';
      const pieces: { seg: typeof whole; open: boolean }[] = [];
      if (override === undefined && gapless && bare.length > 0) {
        let rest: [number, number][] = [[whole.from, whole.from + whole.length]];
        for (const [c0, c1] of bare) {
          rest = rest.flatMap(([a, b]): [number, number][] => (c1 <= a || c0 >= b ? [[a, b]] : [[a, Math.max(a, c0)], [Math.min(b, c1), b]]));
          const a0 = Math.max(whole.from, c0);
          const b0 = Math.min(whole.from + whole.length, c1);
          if (b0 - a0 > EDGE_EPS) pieces.push({ seg: { ...whole, from: a0, length: b0 - a0 }, open: true });
        }
        for (const [a, b] of rest) if (b - a > EDGE_EPS) pieces.push({ seg: { ...whole, from: a, length: b - a }, open: false });
        pieces.sort((p, q) => p.seg.from - q.seg.from);
      } else {
        pieces.push({ seg: whole, open: false });
      }
      for (const { seg, open } of pieces) {
      let choice: TrimChoice;
      let source: TrimRun['source'] = 'auto';
      if (open) {
        choice = { trim: null, none: side === 'bottom' ? 'floor' : 'covered' };
      } else if (override !== undefined && seg.neighbour.kind !== 'covered') {
        source = 'override';
        choice = override === TRIM_NONE ? { trim: null, none: 'override' } : { trim: override };
      } else {
        choice = defaultTrimFor(surface, section, side, seg.neighbour);
      }

      const edge = side === 'top' ? section.y + section.height
        : side === 'bottom' ? section.y
          : side === 'right' ? section.x + section.width
            : section.x;
      const out = side === 'top' || side === 'right' ? 1 : -1;
      const gap = seg.neighbour.kind === 'section' ? seg.neighbour.gap : 0;
      // A trim joining two sections straddles the gap between them.
      const at = choice.trim === TRIM_H ? edge + (out * gap) / 2 : edge;

      runs.push({
        ...seg, surfaceId: surface.id, trim: choice.trim, none: choice.none, color: section.color, source, at,
        owner: 'section', finishes: section.productType,
      });
      }
    }
  }
  return runs;
}

/**
 * The trim around a window or door.
 *
 * An opening is trimmed where it is cut into product, in THAT product's colour:
 * Snap-In Trim J down each side and across the top — and across the bottom too
 * for a window. A door or garage door stands on the floor, so its bottom has
 * none. Where an opening's edge faces bare wall (or another opening) there is
 * no product to finish, so no trim.
 */
export function openingTrims(surface: Surface, opening: Opening): TrimRun[] {
  const runs: TrimRun[] = [];
  for (const side of EDGE_SIDES) {
    const override = opening.edges?.[side];
    const edge = side === 'top' ? opening.y + opening.height
      : side === 'bottom' ? opening.y
        : side === 'right' ? opening.x + opening.width
          : opening.x;
    for (const seg of openingEdgeSegments(surface, opening, side)) {
      const n = seg.neighbour;
      const onProduct = n.kind === 'section';
      const color: ProductColor = onProduct ? n.color : 'White';
      const finishes: SectionProductType = onProduct ? n.productType : 'WCB';

      let choice: TrimChoice;
      let source: TrimRun['source'] = 'auto';
      if (override !== undefined) {
        source = 'override';
        choice = override === TRIM_NONE ? { trim: null, none: 'override' } : { trim: override };
      } else if (side === 'bottom' && openingSitsOnFloor(opening.kind) && opening.y < 1e-6) {
        // A door standing on the floor (pinned, or free and brought down to it).
        choice = { trim: null, none: 'floor' };
      } else if (opening.kind === 'passage' && side === 'top' && opening.y + opening.height >= surface.height - EDGE_EPS) {
        // A wall taken out up to the ceiling: the ceiling carries on over it.
        choice = { trim: null, none: 'covered' };
      } else if (onProduct && opening.kind === 'passage') {
        // Panelling turned through the wall: every edge of the cut is an outside corner.
        choice = { trim: TRIM_OC };
      } else if (onProduct) {
        choice = { trim: TRIM_J };
      } else {
        choice = { trim: null, none: 'bare' };
      }

      runs.push({
        ...seg, surfaceId: surface.id, trim: choice.trim, none: choice.none, color, source, at: edge,
        owner: 'opening', finishes,
      });
    }
  }
  return runs;
}

/** Every trim run on a surface (stretches with no trim included — see `trim`). */
export function surfaceTrims(surface: Surface): TrimRun[] {
  return [
    ...surface.sections.flatMap((section) => sectionTrims(surface, section)),
    ...(surface.openings ?? []).flatMap((opening) => openingTrims(surface, opening)),
  ];
}

/** The trim product for an acronym in a colour, if the loaded retailer carries it. */
export function findTrimProduct(acronym: string, color: ProductColor) {
  return getProductsByType(acronym).find((p) => p.color === color && p.active !== false);
}

/** Acronyms of the trims the loaded retailer carries, in a stable order. */
export const TRIM_ACRONYMS = ['JSnap', 'H', 'Base', 'Cove', 'LOC', 'SOC', 'OCR', 'TC'] as const;

export function availableTrims(color: ProductColor): { acronym: string; name: string }[] {
  return TRIM_ACRONYMS
    .map((acronym) => ({ acronym, product: findTrimProduct(acronym, color) }))
    .filter((t) => t.product)
    .map((t) => ({ acronym: t.acronym, name: t.product!.name }));
}

/**
 * Trim to buy for one surface: total length per trim and colour, rounded up to
 * whole stock lengths. Rounding per surface and per colour is how the estimator
 * counts perimeter trim today (offcuts are used along the same wall).
 */
export function surfaceTrimMaterials(surface: Surface): MaterialItem[] {
  const totals = new Map<string, { acronym: string; color: ProductColor; feet: number }>();
  for (const run of surfaceTrims(surface)) {
    if (!run.trim) continue;
    const key = `${run.trim}|${run.color}`;
    const t = totals.get(key);
    if (t) t.feet += run.length;
    else totals.set(key, { acronym: run.trim, color: run.color, feet: run.length });
  }

  const items: MaterialItem[] = [];
  for (const { acronym, color, feet } of totals.values()) {
    const product = findTrimProduct(acronym, color);
    if (!product) continue; // the retailer doesn't carry it in this colour — nothing to list
    const stock = product.lengthFeet > 0 ? product.lengthFeet : TRIM_STOCK_LENGTH;
    const quantity = Math.ceil((feet - 1e-6) / stock);
    if (quantity <= 0) continue;
    items.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: `Trim_${acronym}`,
    });
  }
  return items;
}

export interface RoomTrimMaterials {
  jTrimsSnapIn: MaterialItem[];
  hTrims: MaterialItem[];
  /** Any other trim a user chose for an edge (base, cove, corners, top cap). */
  otherTrims: MaterialItem[];
}

/** Edge trim for the whole room, sorted into the bill of materials' buckets. */
export function roomTrimMaterials(room: RoomSurfaces): RoomTrimMaterials {
  const out: RoomTrimMaterials = { jTrimsSnapIn: [], hTrims: [], otherTrims: [] };
  for (const surface of roomSurfaceList(room)) {
    for (const item of surfaceTrimMaterials(surface)) {
      if (item.type === `Trim_${TRIM_J}`) out.jTrimsSnapIn.push(item);
      else if (item.type === `Trim_${TRIM_H}`) out.hTrims.push(item);
      else out.otherTrims.push(item);
    }
  }
  return out;
}
